import { createTheme } from '@mantine/core'
import type { MantineColorsTuple } from '@mantine/core'

/**
 * Mantine, wearing our identity.
 *
 * The design system remains tokens.css — this file only teaches Mantine to
 * speak it. That ordering still matters now both apps use the kit: the tokens
 * are what the dashboards and the marketing site read too, neither of which
 * has a kit, so the identity lives somewhere the kit does not. It also keeps
 * the kit swappable, since nothing reads Mantine's palette directly.
 *
 * Shared rather than copied, because the admin and the app are the same product
 * and a second copy is how two products start looking like two products.
 *
 * Mantine's stylesheet is global once imported, so adopting it is a whole-app
 * decision rather than a per-page one.
 */

/**
 * Mantine wants ten steps. Built around the #6a3fd4 accent tokens.css already
 * uses, with --accent at index 6 and --accent-ink at 7, so `color="racedash"`
 * and the token render the same purple.
 */
const racedash: MantineColorsTuple = [
  '#f3eefe',
  '#e4dbfb',
  '#c7b4f4',
  '#a98bee',
  '#8f68e8',
  '#7d51e5',
  '#6a3fd4',
  '#5732b4',
  '#4a2a99',
  '#3c227d',
]

export const theme = createTheme({
  fontFamily: "'Archivo', 'Helvetica Neue', Arial, sans-serif",
  fontFamilyMonospace: "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Consolas, monospace",
  headings: {
    fontFamily: "'Archivo', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: '600',
  },
  defaultRadius: 'sm',
  primaryColor: 'racedash',
  primaryShade: { light: 6, dark: 4 },
  colors: { racedash },
  components: {
    // An admin page is read rather than browsed, so it runs a size below
    // Mantine's default throughout. Set once here rather than as a prop on
    // every control, which is the sort of thing that drifts.
    TextInput: { defaultProps: { size: 'sm' } },
    NumberInput: { defaultProps: { size: 'sm' } },
    Select: { defaultProps: { size: 'sm' } },
    Textarea: { defaultProps: { size: 'sm' } },
    Button: { defaultProps: { size: 'sm' } },
  },
})
